/**
 * useElectronDragGuard
 *
 * Tracks whether a file is being dragged over the window.
 * Used to conditionally hide -webkit-app-region: drag overlays
 * so they don't intercept HTML5 file drag-and-drop.
 *
 * Also prevents Electron's default behavior of navigating to dropped file URLs
 * for drops that land outside the app's own drop zones.
 *
 * Only active when running in Electron.
 */

import { useState, useEffect } from 'react';
import { isElectron } from '@/services/platform';

export function useElectronDragGuard() {
  const [fileDragActive, setFileDragActive] = useState(false);

  useEffect(() => {
    if (!isElectron()) return;

    let dragCounter = 0;

    const handleDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      dragCounter++;
      if (dragCounter === 1) {
        setFileDragActive(true);
      }
    };

    const handleDragLeave = () => {
      dragCounter--;
      if (dragCounter <= 0) {
        dragCounter = 0;
        setFileDragActive(false);
      }
    };

    const handleDrop = (e: DragEvent) => {
      // Only prevent default for drops that weren't already handled
      // by a component's own drop handler (which would have stopped propagation)
      e.preventDefault();
      dragCounter = 0;
      setFileDragActive(false);
    };

    const handleDragOver = (e: DragEvent) => {
      // Must preventDefault on dragover to allow drop (HTML5 DnD spec).
      // Without this, drops outside the MessageArea would navigate.
      e.preventDefault();
    };

    // Use capture phase so we detect the drag BEFORE any component handlers
    document.addEventListener('dragenter', handleDragEnter, true);
    document.addEventListener('dragleave', handleDragLeave, true);
    // dragover and drop use bubble phase so component handlers fire first
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter, true);
      document.removeEventListener('dragleave', handleDragLeave, true);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
      setFileDragActive(false);
    };
  }, []);

  return fileDragActive;
}
